import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { gql } from '@apollo/client';
import { useTranslation } from '@modernloop/i18n';
import { MaybeTooltip } from '@modernloop/shared/components';
import { DescriptionOnIcon, TemplateIcon, WarningIcon } from '@modernloop/shared/icons';
import {
  Button,
  CircularProgress,
  ListItem,
  ListItemButton,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
  TypographyProps,
} from '@mui/material';
import { uniqBy } from 'lodash';
import { useDebouncedCallback } from 'use-debounce';

import {
  TemplateSelectRow_TemplateFragment,
  TemplateType,
  useTemplateSelectTemplateByIdQuery,
  useTemplateSelectTemplatesSearchLazyQuery,
} from 'src/generated/mloop-graphql';

import FilterSelect, { FilterListSelectInterface } from 'src/components/FilterSelect';
import { BaseProps } from 'src/components/types';

import {
  selectButtonLabelSxProps_DO_NOT_USE,
  selectButtonProps_DO_NOT_USE,
} from 'src/constants/selectButtonProps_DO_NOT_USE';

import { DEBOUNCE_TIMEOUT, PAGER_DEFAULT } from 'src/constants';

import TemplateOption from '../SchedulingRequestSettings/TemplateRow/TemplateOption';

export interface Props extends BaseProps {
  minimalUX?: boolean;

  // if we only have templateId, this component will fetch it;  if we already have the template, pass it in to avoid refetch
  selectedTemplateId?: string;
  showErrorOnEmpty?: boolean;

  type: TemplateType;
  /** @deprecated */
  onSelect?: (value: string | undefined) => void;

  onTemplateSelect?: (template: TemplateSelectRow_TemplateFragment) => void;

  // If true, the template preview will be rendered
  showPreviews?: boolean;

  // If true the title will not be rendered
  hideTitle?: boolean;

  // When true, renders using the SelectButton component.
  // When false, renders using a Button or IconButton
  useSelectButton?: boolean;

  labelColor?: TypographyProps['color'];
}

const TemplateSelect: FC<Props> = ({
  minimalUX,
  dataTestId,
  selectedTemplateId,
  type,
  onSelect,
  onTemplateSelect,
  showErrorOnEmpty,
  showPreviews = true,
  hideTitle,
  useSelectButton,
  labelColor,
}: Props) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');

  const filterListSelectInterfaceRef = useRef<FilterListSelectInterface | null>(null);

  const [templateSearchQuery, { data, loading, error }] = useTemplateSelectTemplatesSearchLazyQuery();

  const searchQuery = useCallback(() => {
    templateSearchQuery({
      variables: {
        input: {
          pageInput: {
            limit: PAGER_DEFAULT,
          },
          text: search,
          types: [type],
        },
      },
    });
  }, [search, templateSearchQuery, type]);

  const debouncedSearch = useDebouncedCallback(searchQuery, DEBOUNCE_TIMEOUT);

  // eslint-disable-next-line modernloop/no-use-effect.cjs -- Effect intentionally performs a side effect after render; revisit before adding logic.
  useEffect(() => {}, []);

  const { data: selectedTemplateData, loading: extraTemplatesLoading } = useTemplateSelectTemplateByIdQuery({
    variables: {
      id: selectedTemplateId,
    },
    /**
     * Skip the query till the templates are loaded & update in apollo cache
     */
    skip: loading || !selectedTemplateId,
  });

  const selectedTemplate = useMemo(() => {
    return selectedTemplateData?.template;
  }, [selectedTemplateData]);

  const handleChange = (event: React.ChangeEvent, values: TemplateSelectRow_TemplateFragment[]) => {
    if (!values.length) {
      return;
    }
    if (onSelect) {
      onSelect(values[0].id);
    }
    if (onTemplateSelect) {
      onTemplateSelect(values[0]);
    }
    filterListSelectInterfaceRef.current?.dismiss();
  };
  const getLabel = () => {
    let label = ``;

    if (selectedTemplateId) {
      label = selectedTemplate?.name || '';
    }

    if (selectedTemplate) {
      label = selectedTemplate?.name || '';
    }

    if (!label) {
      label = t('hiring-dashboard-common:common.select.selectTemplate');
    }

    if (useSelectButton) {
      // Render a skeleton on the very first load of all templates
      if (loading && !search) {
        return (
          <Skeleton
            width="100%"
            height={hideTitle ? '32px' : '64px'}
            variant="rectangular"
            style={{ borderRadius: '6px' }}
          />
        );
      }

      const iconColor = showErrorOnEmpty && !selectedTemplateId ? 'error' : 'inherit';

      return (
        <Tooltip title={label} placement="top-start">
          <Button
            {...selectButtonProps_DO_NOT_USE}
            startIcon={
              showErrorOnEmpty && !selectedTemplateId ? (
                <WarningIcon color={iconColor} />
              ) : (
                <TemplateIcon color={iconColor} />
              )
            }
            sx={{
              ...selectButtonProps_DO_NOT_USE.sx,
              justifyContent: 'flex-start',
              border: (theme) =>
                showErrorOnEmpty && !selectedTemplateId ? `1px solid ${theme.palette.error.light}` : '',
              height: 'auto',
            }}
          >
            <Stack alignItems="flex-start" gap={0.5} maxWidth="100%">
              {hideTitle || minimalUX ? undefined : (
                <Typography fontWeight={500} variant="body2">
                  {t('hiring-dashboard-common:common.select.template')}
                </Typography>
              )}
              <Stack direction="row" sx={{ ...selectButtonLabelSxProps_DO_NOT_USE, paddingRight: 5 }}>
                <Typography
                  color={labelColor ?? 'text.primary'}
                  fontWeight={400}
                  sx={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap', maxWidth: '100%' }}
                >
                  {label}
                </Typography>
              </Stack>
            </Stack>
          </Button>
        </Tooltip>
      );
    }

    return (
      <Tooltip title={label}>
        <Button data-testid={dataTestId} size="small" startIcon={<DescriptionOnIcon sx={{ color: 'error.main' }} />}>
          {label && !extraTemplatesLoading ? (
            <Typography variant="body2" fontWeight={500} color="text.secondary" noWrap>
              {label}
            </Typography>
          ) : (
            <CircularProgress size={16} />
          )}
        </Button>
      </Tooltip>
    );
  };

  const renderOption = (props: React.HTMLAttributes<HTMLElement>, template: TemplateSelectRow_TemplateFragment) => {
    let jsx: JSX.Element;
    if (showPreviews) {
      jsx = <TemplateOption template={template} />;
    } else {
      jsx = (
        <div data-testid={template.id} style={{ width: '100%' }}>
          <MaybeTooltip label={template.name || ''} title={template.name || ''} />
        </div>
      );
    }

    return (
      <ListItem {...props}>
        <ListItemButton>{jsx}</ListItemButton>
      </ListItem>
    );
  };

  const options = useMemo(() => {
    const items = data?.templates?.items?.length ? [...data.templates.items] : [];

    if (selectedTemplate) {
      items.unshift(selectedTemplate);
    }

    if (selectedTemplate) {
      items.unshift(selectedTemplate);
    }

    return uniqBy(items, (item) => item.id);
  }, [data?.templates?.items, selectedTemplate]);

  return (
    <FilterSelect
      filterListSelectInterfaceRef={filterListSelectInterfaceRef}
      options={options}
      loading={loading}
      error={error}
      onPopoverOpen={searchQuery}
      placeholderText={t('hiring-dashboard-common:common.select.searchEventTemplates')}
      getLabel={getLabel}
      getOptionLabel={(option): string => {
        return option.name || '';
      }}
      renderOption={renderOption}
      onChange={handleChange}
      onInputChange={(event, value: string) => {
        setSearch(value);
        debouncedSearch();
      }}
    />
  );
};

const TemplateSelectRowFragment = gql`
  ${TemplateOption.fragments.template}
  fragment TemplateSelectRow_template on Template {
    id
    name
    ...TemplateOption_Template
  }
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const TemplateById = gql`
  ${TemplateSelectRowFragment}
  query TemplateSelectTemplateById($id: uuid!) {
    template(id: $id) {
      id
      ...TemplateSelectRow_template
    }
  }
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const TemplatesSearchQuery = gql`
  ${TemplateSelectRowFragment}
  query TemplateSelectTemplatesSearch($input: TemplatesInput!) {
    templates(input: $input) {
      items {
        id
        ...TemplateSelectRow_template
      }
    }
  }
`;

export default TemplateSelect;
